import { getProjectFallbackTarget } from '@/lib/routeGuards'
import { resolveOrgId, resolveTenantId } from '@/lib/routeScope'
import { useRouteSegments } from '@/lib/routerUtils'
import { isPlatformUser, userBelongsToTenant } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { isOwnerOfOrganization } from '@/utils/permissions'
import { useNavigate } from '@tanstack/react-router'
import type React from 'react'
import { useEffect } from 'react'
import { RouteContext } from './route-context'

export type { RouteContext } from './route-context'
export { useRouteContext } from './route-context'

export function RouteContextLayout({ children }: { children: React.ReactNode }) {
	const segments = useRouteSegments()
	const { orgId, tenantId } = segments

	const user = useAuthStore(state => state.user)
	const navigate = useNavigate()

	useEffect(() => {
		if (!user || !orgId) {
			return
		}

		const platformUser = isPlatformUser(user) ? user : null
		if (!platformUser) {
			return
		}

		// Skip redirection for access-denied pages
		if (window.location.pathname.includes('/access-denied')) {
			return
		}

		// A project URL asks for membership; an organization URL asks for ownership.
		const resolvedOrgId = resolveOrgId(orgId)
		const resolvedTenantId = tenantId ? resolveTenantId(tenantId) : undefined
		const hasAccess = resolvedTenantId
			? userBelongsToTenant(platformUser, resolvedTenantId)
			: isOwnerOfOrganization(user, resolvedOrgId)
		if (hasAccess) {
			return
		}

		navigate({ ...getProjectFallbackTarget(platformUser, resolvedOrgId, resolvedTenantId), replace: true })
	}, [user, tenantId, orgId, navigate])

	return <RouteContext.Provider value={segments}>{children}</RouteContext.Provider>
}
